import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { X, Loader2, Link2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const CATEGORIES = [
  { value: 'sim_rigs', label: 'SIM RIGS' },
  { value: 'steering_wheels', label: 'WHEELS' },
  { value: 'pedals', label: 'PEDALS' },
  { value: 'motion_platforms', label: 'MOTION' },
  { value: 'monitors', label: 'DISPLAYS' },
  { value: 'accessories', label: 'BRACKETS / ACCESSORIES' },
  { value: 'gaming_computers', label: 'COMPUTERS' },
];

export default function AddProductFromLinkModal({ onClose, onCreated }) {
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      setError('Paste a supplier product URL first.');
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const res = await base44.functions.invoke('createProductFromSupplierLink', {
        url: url.trim(),
        category: category || undefined,
      });
      if (res.data?.error) {
        setError(res.data.error);
      } else {
        setResult(res.data?.product);
        setTimeout(() => {
          onCreated?.(res.data?.product);
        }, 1200);
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.error || err?.message;
      setError(serverMsg || 'Failed to generate product from link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0d0d0f] border border-white/10 w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-primary" />
            <h2 className="text-white font-black text-sm tracking-widest">ADD PRODUCT FROM SUPPLIER LINK</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-white/40 text-xs leading-relaxed">
            Paste a supplier product URL. The AI reads the page and creates a Square catalog item
            (name, description, price, category, SKU) and mirrors it onto your storefront automatically.
          </p>

          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">SUPPLIER URL <span className="text-red-400">*</span></p>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://supplier.com/product/..."
              className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20"
              disabled={loading}
            />
          </div>

          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">CATEGORY HINT <span className="font-normal text-white/20 normal-case">(optional — AI guesses if blank)</span></p>
            <Select value={category} onValueChange={setCategory} disabled={loading}>
              <SelectTrigger className="bg-[#111115] border-white/10 text-white h-11">
                <SelectValue placeholder="Let AI pick" />
              </SelectTrigger>
              <SelectContent className="bg-[#111115] border-white/10">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} className="text-white focus:bg-primary/20">
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 px-3 py-2">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <p className="text-red-300 text-xs">{error}</p>
            </div>
          )}

          {result && (
            <div className="flex items-start gap-2 bg-green-500/10 border border-green-500/30 px-3 py-2">
              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
              <p className="text-green-300 text-xs">
                Created &quot;{result.name}&quot; (£{Number(result.price).toFixed(2)}) in Square and on the storefront. Refreshed product list…
              </p>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-white/40 hover:text-white text-xs font-bold tracking-wider px-4 py-3">
              CANCEL
            </button>
            <button
              type="submit"
              disabled={loading || !url.trim()}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-5 py-3 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              {loading ? 'GENERATING…' : 'GENERATE & UPLOAD'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}